import React from 'react';
import {
  ChevronRight,
} from 'lucide-react';
import { CTA_COLORS, CTA_LABELS } from './_constants';
import ABTestingPanel from './ABTestingPanel';
import AffiliateClicksCard from './AffiliateClicksCard';
import AprClicksCard from './AprClicksCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CTAAnalyticsTab({ data }) {
  if (!data) return <div className="text-zinc-500 text-center py-12">Loading CTA analytics...</div>;

  const { total_clicks, clicks_by_cta, clicks_by_device, clicks_by_page, daily_trend, funnel } = data;
  const maxClicks = clicks_by_cta?.[0]?.clicks || 1;

  return (
    <div className="space-y-8" data-testid="cta-analytics-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-zinc-900 border border-zinc-800 p-4">
          <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Total CTA Clicks</p>
          <p className="text-white text-2xl font-bold" data-testid="total-cta-clicks">{total_clicks || 0}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 p-4">
          <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Homepage CTAs</p>
          <p className="text-white text-2xl font-bold">{funnel?.homepage_cta_clicks || 0}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 p-4">
          <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Content Clicks</p>
          <p className="text-white text-2xl font-bold">{funnel?.content_clicks || 0}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 p-4">
          <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Affiliate Clicks</p>
          <p className="text-[#c8ff00] text-2xl font-bold">{funnel?.affiliate_clicks || 0}</p>
        </div>
      </div>

      <AprClicksCard />

      <AffiliateClicksCard />

      {/* CTA Performance Ranking — Horizontal Bar Chart */}
      <div className="bg-zinc-900 border border-zinc-800 p-6">
        <h3 className="text-white text-lg font-semibold mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
          CTA Performance Ranking
        </h3>
        {clicks_by_cta?.length > 0 ? (
          <div className="space-y-3">
            {clicks_by_cta.map((cta, i) => {
              const pct = (cta.clicks / maxClicks) * 100;
              const color = CTA_COLORS[cta.name] || '#71717a';
              const label = CTA_LABELS[cta.name] || cta.name;
              return (
                <div key={cta.name} data-testid={`cta-bar-${cta.name}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-zinc-400 text-xs w-5 text-right">{i + 1}.</span>
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                      <span className="text-zinc-300 text-sm">{label}</span>
                    </div>
                    <span className="text-white text-sm font-bold">{cta.clicks}</span>
                  </div>
                  <div className="h-2 bg-zinc-800 rounded-full overflow-hidden ml-7">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${pct}%`, backgroundColor: color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-zinc-600 text-sm text-center py-8">No CTA click data yet. Clicks will appear here as users interact with your CTAs.</p>
        )}
      </div>

      {/* Two column: Device Breakdown + Source Pages */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Device Breakdown */}
        <div className="bg-zinc-900 border border-zinc-800 p-6">
          <h3 className="text-white text-lg font-semibold mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            Device Breakdown
          </h3>
          {clicks_by_device?.length > 0 ? (
            <div className="space-y-3">
              {clicks_by_device.map((d) => {
                const deviceMax = clicks_by_device[0]?.clicks || 1;
                const pct = (d.clicks / deviceMax) * 100;
                const colors = { desktop: '#3b82f6', mobile: '#22c55e', tablet: '#f59e0b' };
                return (
                  <div key={d.device}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-zinc-300 text-sm capitalize">{d.device}</span>
                      <span className="text-white text-sm font-bold">{d.clicks}</span>
                    </div>
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${pct}%`, backgroundColor: colors[d.device] || '#71717a' }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-zinc-600 text-sm text-center py-4">No device data yet</p>
          )}
        </div>

        {/* Top Source Pages */}
        <div className="bg-zinc-900 border border-zinc-800 p-6">
          <h3 className="text-white text-lg font-semibold mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            Top Source Pages
          </h3>
          {clicks_by_page?.length > 0 ? (
            <div className="space-y-2">
              {clicks_by_page.map((p, i) => (
                <div key={p.page} className="flex items-center justify-between py-1.5 border-b border-zinc-800/50 last:border-0">
                  <span className="text-zinc-400 text-sm truncate max-w-[200px]">{p.page || '/'}</span>
                  <span className="text-white text-sm font-bold">{p.clicks}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-zinc-600 text-sm text-center py-4">No page data yet</p>
          )}
        </div>
      </div>

      {/* Daily Trend */}
      <div className="bg-zinc-900 border border-zinc-800 p-6">
        <h3 className="text-white text-lg font-semibold mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
          Daily Click Trend (Last 30 Days)
        </h3>
        {daily_trend?.length > 0 ? (
          <div className="flex items-end gap-1 h-40 overflow-x-auto">
            {daily_trend.map((d) => {
              const trendMax = Math.max(...daily_trend.map(t => t.clicks));
              const h = trendMax > 0 ? (d.clicks / trendMax) * 100 : 0;
              return (
                <div key={d.date} className="flex flex-col items-center gap-1 min-w-[24px]" title={`${d.date}: ${d.clicks} clicks`}>
                  <span className="text-zinc-500 text-[9px]">{d.clicks}</span>
                  <div
                    className="w-4 rounded-t transition-all duration-300"
                    style={{ height: `${Math.max(h, 4)}%`, backgroundColor: '#E63946' }}
                  />
                  <span className="text-zinc-600 text-[8px] rotate-[-45deg] whitespace-nowrap">{d.date.slice(5)}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-zinc-600 text-sm text-center py-8">No trend data yet. Daily click data will appear here.</p>
        )}
      </div>

      {/* Conversion Funnel */}
      <div className="bg-zinc-900 border border-zinc-800 p-6">
        <h3 className="text-white text-lg font-semibold mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
          Conversion Funnel
        </h3>
        <div className="flex items-center justify-center gap-4 flex-wrap">
          {[
            { label: 'Homepage CTA Clicks', value: funnel?.homepage_cta_clicks || 0, color: '#3b82f6' },
            { label: 'Content Clicks', value: funnel?.content_clicks || 0, color: '#f59e0b' },
            { label: 'Affiliate Clicks', value: funnel?.affiliate_clicks || 0, color: '#c8ff00' },
          ].map((step, i) => (
            <React.Fragment key={step.label}>
              <div className="text-center">
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center mb-2 mx-auto"
                  style={{ backgroundColor: `${step.color}15`, border: `2px solid ${step.color}40` }}
                >
                  <span className="text-xl font-bold" style={{ color: step.color }}>{step.value}</span>
                </div>
                <span className="text-zinc-400 text-xs">{step.label}</span>
              </div>
              {i < 2 && <ChevronRight className="w-5 h-5 text-zinc-700 shrink-0" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* A/B Testing Section */}
      <ABTestingPanel />
    </div>
  );
}
